import React, { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Tooltip,
  Typography,
  Paper,
  Collapse,
  IconButton,
} from "@mui/material";

// MUI icons for UI interactions
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";

// Components and functions from project
import CommonAccordion from "../../custom-components/CommonAccordion";
import CommonTable from "../../custom-components/CommonTestCasesTable";
import CommonDownload from "../../custom-components/CommonDownload";
import CommonCopy from "../../custom-components/CommonCopy";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import {
  MultipleTestCasesContext,
  TestCasesLoaderContext,
} from "../../main-pages/MultiTestCaseGenerationPage";
import {
  generateTestCasesUsingFSDAndUseCase,
  generateTraceabilityMatrix,
} from "../../../services/webServices";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";

const MultipleTestTechniquesComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setLoadingState } = useContext(LoadingModalContext);
  const { testCaseLoaderState, setTestCaseLoaderState } = useContext(
    TestCasesLoaderContext
  );
  const multipleTestCasesContext = useContext(MultipleTestCasesContext);
  const multipleUseCasesFromContext = multipleTestCasesContext.multipleUseCases;
  const selectedUseCasesFromContext = multipleTestCasesContext.selectedUseCases;
  const functionalSpecDocument =
    multipleTestCasesContext.functionalSpecDocument;
  const {
    testConditions,
    testingTechniques,
    traceabilityMatrix,
    multiTestCases,
    setMultiTestCases,
  } = multipleTestCasesContext;
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [multipleUseCase, setMultipleUseCase] = useState(
    multipleUseCasesFromContext
  );
  const [selectedUseCase, setSelectedUseCase] = useState(
    selectedUseCasesFromContext
  );
  const [activeThumb, setActiveThumb] = useState(null);
  const [noneUseCaseSelected, setNoneUseCaseSelected] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [editableTechniques, setEditableTechniques] =
    useState(testingTechniques);

  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  const handleDownload = () => {
    setDownloadOpen(true);
  };

  const defaultTestTechniquesExpanded =
    Object.entries(testingTechniques || {}).length > 0 &&
    Object.entries(traceabilityMatrix).length === 0;

  // const handleMultipleTestCases = async () => {
  //   let progressHandler;
  //   if (selectedUseCase.every((eachUseCase) => !eachUseCase)) {
  //     setNoneUseCaseSelected(true);
  //     return;
  //   }
  //   setMultiTestCases([]);
  //   try {
  //     progressHandler = handleProgressBar({
  //       setLoadingState,
  //       totalDurationMs: 120000,
  //       messageList: ["Generating Functional Test Cases", "Generating Non-Functional Test Cases", "Generating Traceability Matrix"],
  //       messageDurations: [55000, 50000, 15000]
  //     });
  //     progressHandler.start();
  //     setTestCaseLoaderState("Test Cases");

  //     // Create an array of promises for concurrent calls
  //     const testCasePromises = selectedUseCase.map(async (eachUseCase, index) => {
  //       const eachTestCondition = testConditions[index];

  //       if (
  //         eachUseCase &&
  //         Object.keys(eachUseCase).length > 0 &&
  //         eachTestCondition &&
  //         Object.keys(eachTestCondition).length > 0
  //       ) {
  //         const requestData = {
  //           testConditions: eachTestCondition,
  //           useCases: eachUseCase,
  //         };
  //         try {
  //           const response = await generateTestCasesUsingFSDAndUseCase(requestData);
  //           return response?.data?.testcases || null;
  //         } catch (error) {
  //           console.error(`Error generating test cases for index ${index}:`, error);
  //           return null;
  //         }
  //       }
  //       return null;
  //     });

  //     const allGeneratedTestCases = await Promise.all(testCasePromises);

  //     const testCasesArray = allGeneratedTestCases.flatMap(
  //       (testCases) => testCases || []
  //     );

  //     setMultiTestCases(testCasesArray);

  //     const testCasesDetailsForRTM = testCasesArray.map((eachTestCase) => {
  //       return {
  //         "Test Case ID": eachTestCase["Test Case ID"],
  //         "Test Case Title": eachTestCase["Test Case Title"],
  //         "Test Objective": eachTestCase["Test Objective"],
  //       };
  //     });

  //     const traceabilityMatrix = await generateTraceabilityMatrix({
  //       functionalSpecDocument: functionalSpecDocument,
  //       testCaseDetails: testCasesDetailsForRTM,
  //     });
  //     await progressHandler.finishAndClose();
  //     navigate("/dashboard/generate-multi/testcases/output", {
  //       state: {
  //         ...location.state,
  //         multipleUseCases: multipleUseCase,
  //         selectedMultipleUseCases: selectedUseCase,
  //         multipleTestCases: testCasesArray,
  //         selectedMultipleTestCases: Array(testCasesArray.length).fill(null),
  //         traceabilityMatrix: traceabilityMatrix.data,
  //       },
  //       replace: true,
  //     });
  //   } catch (error) {
  //     console.error("Error", error);
  //     setLoadingState({ open: false, message: "", progress: null });
  //   } finally {
  //     setTestCaseLoaderState(null);
  //     if (progressHandler) {
  //       progressHandler.stop();
  //     }
  //     setLoadingState({ open: false, message: "", progress: null });
  //   }
  // };

  const tableDataConverter = (techniqueArray) => {
    if (!Array.isArray(techniqueArray) || techniqueArray.length === 0) {
      return { headers: [], values: [] };
    }

    // Filter out null/undefined arrays and check if first element exists
    const validArrays = techniqueArray.filter(
      (arr) =>
        Array.isArray(arr) &&
        arr.length > 0 &&
        arr[0] &&
        typeof arr[0] === "object"
    );

    if (validArrays.length === 0) {
      return { headers: [], values: [] };
    }

    const headers = Object.keys(validArrays[0][0]);
    const values = [];
    validArrays.forEach((eachArray) => {
      if (Array.isArray(eachArray)) {
        eachArray.forEach((eachCase) => {
          if (eachCase && typeof eachCase === "object") {
            values.push(Object.values(eachCase));
          }
        });
      }
    });
    return { headers, values };
  };

  const handleTestTechniquesTableData = (techniqueArray) => {
    if (!Array.isArray(techniqueArray) || techniqueArray.length === 0) {
      console.log("EMPTY OR INVALID techniqueArray:", techniqueArray);
      return { headers: [], values: [], tableData: [] };
    }

    // Filter out null/undefined values and flatten safely
    const flatArray = techniqueArray
      .filter((arr) => Array.isArray(arr))
      .flat()
      .filter((obj) => obj && typeof obj === "object");

    if (flatArray.length === 0) {
      return { headers: [], values: [], tableData: [] };
    }

    const deepCopiedFlatArray = flatArray.map((obj) => ({ ...obj }));
    const headers = Object.keys(deepCopiedFlatArray[0]);
    const values = deepCopiedFlatArray.map((obj) =>
      headers.map((key) => obj[key])
    );
    return {
      headers,
      values,
      tableData: deepCopiedFlatArray,
    };
  };

  // Generate Inner Accordions for each requirement
  const RenderInnerAccordions = ({
    testingTechniques,
    handleTestTechniquesTableData,
    setEditableTechniques,
    openSections,
    setOpenSections,
  }) => {
    const toggleSection = (key) => {
      setOpenSections((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    };
    return (
      <>
        {Object.entries(testingTechniques || {})
          .filter(
            ([requirement]) => requirement !== "Risk-prioritized scenarios"
          )
          .map(([requirement, techniques]) => {
            const isOpen = !!openSections[requirement];
            console.log("🔁 Rendering Accordion for requirement:", requirement);
            console.log("🔥 Techniques being passed:", techniques);

            const { headers, values, tableData } =
              handleTestTechniquesTableData(techniques);

            return (
              <Paper
                key={requirement}
                elevation={0}
                sx={{
                  mb: 2,
                  p: 1,
                  borderRadius: 1,
                  border: "1px solid #ccc",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSection(requirement)}
                >
                  <Typography sx={{ fontSize: "16px" }}>
                    {requirement}
                  </Typography>
                  <IconButton size="small">
                    {isOpen ? (
                      <ArrowDropUpIcon sx={{ color: "black" }} />
                    ) : (
                      <ArrowDropDownIcon sx={{ color: "black" }} />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <CommonTable
                    headers={headers}
                    values={values}
                    tableData={tableData}
                    setTableData={(updater) => {
                      setEditableTechniques((prevTechniques) => {
                        const prevData = prevTechniques[requirement]?.[0] || [];
                        const updated =
                          typeof updater === "function"
                            ? updater(prevData)
                            : updater;

                        console.log("✅ Updating technique:", requirement);
                        console.log("🧾 New data:", updated);

                        return {
                          ...prevTechniques,
                          [requirement]: [updated],
                        };
                      });
                    }}
                    edittable={true}
                  />
                </Collapse>
              </Paper>
            );
          })}
      </>
    );
  };

  return (
    <Box>
      {/* Main Accordion */}
      <CommonAccordion
        summary={"Test Techniques"}
        details={
          <Box>
            <RenderInnerAccordions
              testingTechniques={editableTechniques}
              handleTestTechniquesTableData={handleTestTechniquesTableData}
              setEditableTechniques={setEditableTechniques}
              openSections={openSections}
              setOpenSections={setOpenSections}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginTop: 7,
                marginBottom: 2,
              }}
            >
              {/* Thumbs Up Button with Tooltip */}
              <Tooltip title="Like" arrow>
                <Box
                  onClick={() => handleThumbClick("like")}
                  sx={{
                    cursor: "pointer",
                    marginRight: 2,
                    "&:hover": {
                      color: "#49A3FF",
                    },
                  }}
                >
                  {activeThumb === "like" ? (
                    <ThumbUpIcon sx={{ fontSize: "24px" }} />
                  ) : (
                    <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} />
                  )}
                </Box>
              </Tooltip>

              {/* Thumbs Down Button with Tooltip */}
              <Tooltip title="Dislike" arrow>
                <Box
                  onClick={() => handleThumbClick("dislike")}
                  sx={{
                    cursor: "pointer",
                    marginRight: 2,
                    "&:hover": {
                      color: "#FF4842",
                    },
                  }}
                >
                  {activeThumb === "dislike" ? (
                    <ThumbDownIcon sx={{ fontSize: "24px" }} />
                  ) : (
                    <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} />
                  )}
                </Box>
              </Tooltip>
              <CommonCopy contentToCopy={testConditions} />
            </Box>
          </Box>
        }
        type={"output"}
        defaultExpanded={defaultTestTechniquesExpanded}
        buttons={[
          {
            label: "Download",
            variant: "text",
            onClick: handleDownload,
            startIcon: <FileDownloadOutlinedIcon />,
            sx: { textTransform: "none" },
          },
          {
            label: "Select Testing Types",
            variant: "contained",
            onClick: () => {
              setTestCaseLoaderState("Select Testing Types");
              navigate(
                "/dashboard/generate-multi/testcases/select-testing-types",
                {
                  state: { ...location.state },
                  replace: true,
                }
              );
            },

            sx: { float: "right" },
            disabled:
              testCaseLoaderState === "Test Cases" ||
              multipleUseCase.filter(Boolean).length === 0 ||
              selectedUseCase.filter(Boolean).length === 0,
          },
        ]}
        fullScreenMode={true}
      />
      <CommonDownload
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        serviceName={"Test Techniques"}
        serviceResponse={[
          // { heading: "Test Techniques", type: "text", data: "" },
          ...Object.entries(testingTechniques || {}).map(([key, value]) => ({
            heading: key,
            type: "table",
            data: { ...tableDataConverter(value) },
          })),
        ]}
        wordData={[
          // { heading: "Test Techniques", type: "text", data: "" },
          ...Object.entries(testingTechniques || {}).map(([key, value]) => ({
            heading: key,
            type: "table",
            data: { ...tableDataConverter(value) },
          })),
        ]}
        documents={["docx", "pdf"] || []}
      />
    </Box>
  );
};

export default MultipleTestTechniquesComponent;
